import React, { useState, useRef, useEffect } from 'react';
import { submitQuiz } from './actions';
import { quizPageProps, QuizResult, QuizAnswer } from '../../../interfaces/screens/quizPage';
import { motion } from 'framer-motion';

/**
 * QuizPage component manages the quiz experience, including question navigation,
 * answer selection, animations, and score display after submission.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.quizData - Quiz data including id, questions, etc.
 * @param {Function} props.onClose - Function to call when closing the quiz
 */
const QuizPage: React.FC<quizPageProps> = ({ quizData, onClose }) => {
  const { id, questions, onAssessmentComplete, sectionId } = quizData;

  // State variables for quiz progress and result
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(true);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const [startTime] = useState<number>(Date.now());
  const [quizDuration, setQuizDuration] = useState<string>('0:00');
  const [animatedScore, setAnimatedScore] = useState(0);

  // Refs for tracking question transition state
  const transitioningRef = useRef(false);

  // Add this constant at the top of the component
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];

  // Get current question
  const question = questions?.length ? questions[currentQuestionIndex] : null;
  const totalQuestions = questions?.length || 0;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const questionVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.25, // Faster animation
        ease: [0.4, 0, 0.2, 1],
      },
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2, // Faster animation
        ease: [0.4, 0, 1, 1],
      },
    }),
  };

  // Handle entrance animation on component mount
  useEffect(() => {
    setIsOpening(true);
    const timer = setTimeout(() => {
      setIsOpening(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Calculate duration when quiz is finished
  useEffect(() => {
    if (isFinished) {
      const endTime = Date.now();
      const durationInSeconds = Math.floor((endTime - startTime) / 1000);
      const minutes = Math.floor(durationInSeconds / 60);
      const seconds = durationInSeconds % 60;
      setQuizDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }
  }, [isFinished, startTime]);

  // Animate score when quiz is finished
  useEffect(() => {
    if (isFinished && quizResult) {
      const finalScore = Math.round((quizResult.score / quizResult.questions_count) * 100);

      // First animate quickly to finalScore - 3
      const timer1 = setTimeout(() => {
        setAnimatedScore(Math.max(0, finalScore - 3));
      }, 200);

      // Then animate the last 3 points one by one
      const timer2 = setTimeout(() => {
        setAnimatedScore((prev) => Math.min(finalScore, prev + 1));
      }, 1000);

      const timer3 = setTimeout(() => {
        setAnimatedScore((prev) => Math.min(finalScore, prev + 1));
      }, 1300);

      const timer4 = setTimeout(() => {
        setAnimatedScore((prev) => Math.min(finalScore, prev + 1));
      }, 1600);

      return () => {
        [timer1, timer2, timer3, timer4].forEach((timer) => clearTimeout(timer));
      };
    }
  }, [isFinished, quizResult]);

  // Record answer without navigation
  const recordAnswer = (index: number) => {
    setSelectedOptionIndex(index);

    if (question?.options?.length && question?.options[index]?.id) {
      const selectedOptionId = question.options[index]?.id;

      // Update answers by replacing any existing answer for this question
      setAnswers((prev) => {
        const newAnswers = prev.filter((a) => a.question_id !== question.id);
        return [...newAnswers, { question_id: question.id, answer_ids: [selectedOptionId] }];
      });
    }
  };

  // Handle option selection
  const handleOptionSelect = (index: number) => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;

    // Record the answer
    recordAnswer(index);

    // Shorter delay to show selection - faster UX
    setTimeout(() => {
      // If last question, submit the quiz after ensuring the answer is recorded
      if (isLastQuestion) {
        // Force immediate submission without waiting for state updates
        const currentAnswers = [...answers];

        // Ensure the current question's answer is included
        if (question?.options?.[index]?.id) {
          const selectedOptionId = question.options[index].id;
          const hasCurrentAnswer = currentAnswers.some((a) => a.question_id === question.id);

          if (!hasCurrentAnswer) {
            currentAnswers.push({
              question_id: question.id,
              answer_ids: [selectedOptionId],
            });
          }
        }

        // Submit with the current answers
        submitQuiz(id, currentAnswers)
          .then((response) => {
            if (response) {
              setQuizResult(response);
              setIsFinished(true);
            } else {
              console.error('No response from quiz submission');
            }
          })
          .catch((error) => {
            console.error('Error submitting quiz:', error);
            setIsClosing(true);
            setTimeout(() => onClose(), 300);
          })
          .finally(() => {
            setIsLoading(false);
            transitioningRef.current = false;
          });

        setIsLoading(true);
        return;
      }

      // Otherwise move to next question
      setSlideDirection('next');
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOptionIndex(null);

      setTimeout(() => {
        transitioningRef.current = false;
      }, 300); // Faster transition reset
    }, 400); // Shorter delay for faster response
  };

  // Handle close with exit animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Handle completion acknowledgment
  const handleDone = () => {
    if (quizResult?.success && onAssessmentComplete && sectionId) {
      onAssessmentComplete(sectionId);
    }
    handleClose();
  };

  // Enhanced close handling
  const handleAttemptClose = () => {
    if (isFinished || isLoading) {
      handleClose();
      return;
    }

    if (window.confirm('Are you sure you want to exit the quiz? Your progress will be lost.')) {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 300);
    }
  };

  // Enhanced Quiz Results with AI-style background
  const renderQuizResults = () => {
    if (!isFinished || !quizResult) return null;

    const finalScore = Math.round((quizResult.score / quizResult.questions_count) * 100);
    const scoreColor = finalScore >= 80 ? 'emerald' : finalScore >= 60 ? 'blue' : 'amber';
    const scoreMessage = finalScore >= 80 ? 'Excellent!' : finalScore >= 60 ? 'Good job!' : 'Keep practicing!';
    const correctAnswers = quizResult.score;

    // Dynamic gradient positions for animation
    const gradientPositions = {
      emerald: {
        primary: 'after:bg-emerald-500/30',
        secondary: 'before:bg-emerald-400/20',
        accent: 'bg-emerald-300/10',
      },
      blue: {
        primary: 'after:bg-blue-500/30',
        secondary: 'before:bg-blue-400/20',
        accent: 'bg-blue-300/10',
      },
      amber: {
        primary: 'after:bg-amber-500/30',
        secondary: 'before:bg-amber-400/20',
        accent: 'bg-amber-300/10',
      },
    };

    return (
      <div className="relative flex flex-col items-center justify-center h-full overflow-hidden">
        {/* AI-style animated background */}
        <div
          className={`absolute inset-0 w-full h-full ${gradientPositions[scoreColor].accent}
            before:absolute before:inset-0 
            before:blur-[100px] before:opacity-70 before:animate-pulse
            before:rounded-full before:translate-x-[10%] before:translate-y-[10%]
            ${gradientPositions[scoreColor].secondary}
            after:absolute after:inset-0 
            after:blur-[70px] after:opacity-70 after:animate-pulse
            after:rounded-full after:translate-x-[-5%] after:translate-y-[-5%]
            ${gradientPositions[scoreColor].primary}`}
        />

        {/* Mesh gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.1),rgba(255,255,255,0))]" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-lg text-center p-8 backdrop-blur-sm"
        >
          {/* Animated Score Display */}
          <div className="mb-8">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center justify-center mb-4"
            >
              <div className="relative">
                <span
                  className={`text-8xl font-bold text-${scoreColor}-500 dark:text-white tabular-nums
                               [text-shadow:0_0_30px_rgba(0,0,0,0.1)]`}
                >
                  {animatedScore}
                </span>
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className={`absolute -right-8 top-4 text-3xl dark:text-white font-medium text-${scoreColor}-500`}
                >
                  %
                </motion.span>
              </div>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className={`text-xl font-medium text-${scoreColor}-500 dark:text-white mb-2
                         [text-shadow:0_0_20px_rgba(0,0,0,0.1)]`}
            >
              {scoreMessage}
            </motion.h2>
          </div>

          {/* Stats Row with glass effect */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex justify-center gap-12 mb-10"
          >
            <div className="text-center px-6 py-3 rounded-2xl bg-white/5 backdrop-blur-md">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Correct</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {correctAnswers}/{quizResult.questions_count}
              </p>
            </div>
            <div className="text-center px-6 py-3 rounded-2xl bg-white/5 backdrop-blur-md">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Time</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{quizDuration}</p>
            </div>
          </motion.div>

          {/* Action Button with glass effect */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            onClick={handleDone}
            className={`bg-primary px-8 py-3 bg-${scoreColor}-500/80 backdrop-blur-sm hover:bg-${scoreColor}-500 
                     text-white rounded-xl font-medium 
                     transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg
                     focus:outline-none focus:ring-2 focus:ring-${scoreColor}-500 focus:ring-offset-2 
                     dark:focus:ring-offset-gray-800
                     [box-shadow:0_0_20px_rgba(0,0,0,0.1)]`}
          >
            Complete Quiz
          </motion.button>
        </motion.div>
      </div>
    );
  };

  // Main quiz view with enhanced animations
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-300 ${
        isClosing ? 'bg-opacity-0' : isOpening ? 'bg-opacity-0' : 'bg-opacity-50'
      }`}
      style={{ backdropFilter: isOpening ? 'none' : 'blur(5px)' }}
    >
      <div
        className={`relative w-[95%] max-w-4xl h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isClosing
            ? 'opacity-0 scale-95 translate-y-4'
            : isOpening
              ? 'opacity-0 scale-95 translate-y-4'
              : 'opacity-100 scale-100 translate-y-0'
        }`}
      >
        {/* Header with simplified design */}
        <div className="px-6 py-4 flex justify-between items-center border-b border-gray-100 dark:border-gray-800">
          <button
            onClick={handleAttemptClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-600 dark:text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {currentQuestionIndex + 1} / {totalQuestions}
          </div>
        </div>

        {/* Progress bar with smoother animation */}
        <motion.div
          className="h-1 bg-primary/10"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress / 100 }}
          transition={{ duration: 0.3 }}
          style={{ transformOrigin: 'left' }}
        />

        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Submitting Quiz...</h2>
              <p className="text-gray-600 dark:text-gray-400">Please wait</p>
            </div>
          </div>
        )}

        {/* Quiz Results or Questions */}
        {isFinished ? (
          renderQuizResults()
        ) : (
          <div className="h-[calc(90vh-120px)] overflow-y-auto">
            <motion.div
              key={currentQuestionIndex}
              custom={slideDirection === 'next' ? 1 : -1}
              variants={questionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="flex flex-col p-6 md:p-8 gap-8"
            >
              {/* Question section */}
              <div className="flex flex-col">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  {question?.content}
                </h2>

                {/* Removed previous button as requested */}
              </div>

              {/* Options section */}
              <div>
                <div className="space-y-4">
                  {question?.options?.map((option, index) => (
                    <motion.button
                      key={option.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.25,
                          delay: index * 0.05, // Reduced delay between options
                          ease: [0.4, 0, 0.2, 1],
                        },
                      }}
                      onClick={() => handleOptionSelect(index)}
                      disabled={selectedOptionIndex !== null && !isLastQuestion}
                      className={`w-full p-4 rounded-xl text-left transition-all duration-300 flex items-center gap-4 ${
                        selectedOptionIndex === index
                          ? 'bg-primary text-white shadow-lg shadow-primary/25 transform scale-[1.02]'
                          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white hover:transform hover:scale-[1.02]'
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          selectedOptionIndex === index
                            ? 'bg-white text-primary'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {optionLetters[index]}
                      </span>
                      <span className="flex-1">{option.content}</span>
                    </motion.button>
                  ))}
                </div>

                {/* Removed submit button as it's now automatic */}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPage;
